import { Box, Card, Typography, Divider, Button, Grid } from '@mui/material';
import { AlertCircle, ArrowLeft, ClipboardList, Clock3, FileText, Scale, Settings, Sparkles, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function InfoRow({ icon: Icon, title, description, color, bgColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: 'rgba(255,255,255,0.7)'
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AderReportAiAssistito() {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/ader/coda-validazione')}>
          Coda validazione
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
          ADER · Report AI-assistito
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Prescrizione e regolarità della notifica: requisiti minimi, limiti dell’AI e cosa serve per renderlo affidabile
        </Typography>
      </Card>

      <Card sx={{ p: 2 }}>
        <InfoRow
          icon={AlertCircle}
          title="Nota importante"
          description="Il report può essere AI‑assistito, ma prescrizione e regolarità della notifica richiedono dati minimi, regole giuridiche parametrizzate e prove documentali. Sull’estratto di ruolo, spesso l’AI non ha abbastanza elementi per concludere in modo affidabile."
          color="#b45309"
          bgColor="#fef3c7"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Requisiti informativi (cosa deve avere il sistema)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={FileText}
              title="Per ogni riga/partita"
              description="Numero atto/cartella/avviso, ente creditore, tipologia entrata (IRPEF/INPS/bollo/multa…), anno di imposta/periodo, importi, stato e date chiave."
              color="#0284c7"
              bgColor="#e0f2fe"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={ClipboardList}
              title="Notifica"
              description="Non basta la “data notifica”: servono relata/AR/PEC (esito, indirizzo, destinatario, compiuta giacenza) e verifiche su vizi tipici (soggetto diverso, indirizzo errato, irreperibilità, tempi e modalità)."
              color="#16a34a"
              bgColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={Clock3}
              title="Prescrizione"
              description="Servono interruzioni/sospensioni (intimazioni, solleciti, pignoramenti, rateizzazioni, sospensioni giudiziali) con date e prove; altrimenti l’esito resta “non determinabile / da verificare”."
              color="#7c3aed"
              bgColor="#ede9fe"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={Scale}
              title="Regime applicabile"
              description="La prescrizione cambia per natura del credito e casi particolari; il sistema deve classificare la “famiglia” della riga."
              color="#0f766e"
              bgColor="#ccfbf1"
            />
          </Grid>
          <Grid item xs={12}>
            <InfoRow
              icon={Sparkles}
              title="Data di riferimento e ambito"
              description="Data di valutazione, eventuale domicilio fiscale/residenza/PEC e se persona fisica/giuridica (alcuni controlli cambiano)."
              color="#be185d"
              bgColor="#fce7f3"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Requisiti logici (come farlo bene)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={Settings}
              title="Motore regole deterministico"
              description="Termine base per tipo credito, timeline (atto → notifica → interruzioni) ed esito “prescritta / non prescritta / non determinabile”."
              color="#1d4ed8"
              bgColor="#dbeafe"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={Sparkles}
              title="AI come supporto"
              description="Estrattore campi dai PDF allegati, classificatore della famiglia credito quando non esplicita, redattore del report (spiegazione + checklist) citando i dati utilizzati."
              color="#7c3aed"
              bgColor="#ede9fe"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={UserCheck}
              title="Confidence + validazione umana"
              description="Se mancano prove o la confidence è bassa, invio in coda validazione con motivazione (“manca relata”, “mancano atti interruttivi”, ecc.)."
              color="#16a34a"
              bgColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              icon={ClipboardList}
              title="Auditability"
              description="Log di input, fonti, regole applicate, output e versioni per tracciabilità e difendibilità."
              color="#0284c7"
              bgColor="#e0f2fe"
            />
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
